import {
  Card,
  Divider,
  DownloadIcon,
  GKButton,
  GKModal,
  GKSearchSelect,
  Grid2,
  ReloadIcon,
  RupeeIcon,
  Show,
  Typography,
} from "components";
import { useFetchQuery } from "hooks/useQueries";
import { ClientBasketSubscriptionDetails } from "interfaces/ClientBasket";
import { orderBy } from "lodash";
import { financialYearFilter } from "Pages/RevenueManagement/utils";
import { useMemo, useState } from "react";
import { numberToCurrency } from "utils";
import { GKModalProps } from "utils/commonInterface";
import { handleDownloadFile, matchDate } from "utils/helpers";

interface Props {
  basketId: number;
}

const SubscriptionDetailsModal = (props: GKModalProps & Props) => {
  const { open, setOpen, basketId } = props;

  const [selectedFY, setSelectedFY] = useState<string | null>("2025-2026");

  const { data, isFetching } = useFetchQuery({
    key: ["FETCH_CLIENT_BASKETS_SUBSCRIPTION"],
    route: `/client/subscriptions/?basket_id=${basketId}`,
  });

  const subscriptionMap = data?.data?.data?.financialYears || {};

  const subscriptionList = useMemo(() => {
    const cleanFY = selectedFY?.replace(/^FY/, "");
    return cleanFY && subscriptionMap[cleanFY] ? subscriptionMap[cleanFY] : [];
  }, [selectedFY, subscriptionMap]);

  return (
    <GKModal
      modalTitle={"My Subscriptions Details"}
      open={open}
      setOpen={setOpen}
      width={"580px"}
      maxHeight="calc(100vh - 200px)"
      loading={isFetching}
    >
      <Grid2 container spacing={2}>
        <Grid2
          container
          size={12}
          spacing={2}
          display={"flex"}
          alignItems={"center"}
        >
          <Grid2 size={4}>
            <GKSearchSelect
              options={orderBy(financialYearFilter, "name", "desc")}
              getOptionLabel={(option) => option.name || option}
              placeholder="financial year"
              value={selectedFY ? { name: selectedFY } : null}
              onChange={(_, val) => setSelectedFY(val?.name || null)}
            />
          </Grid2>
          <Grid2 size={8}>
            <Typography
              fontSize={12}
              fontWeight={500}
              color="var(--neutral-500)"
              display={"flex"}
              alignItems={"center"}
              gap={0.5}
            >
              Total Entries:
              <Typography
                fontSize={12}
                fontWeight={600}
                color={"var(--neutral-500)"}
              >
                {data?.data?.data?.subscriptionDetails?.totalEntries}
              </Typography>
            </Typography>
          </Grid2>
        </Grid2>

        <Grid2 size={12}>
          <Card sx={{ p: 3, borderRadius: "6px", background: "#F8F8F8" }}>
            <Grid2 container spacing={4}>
              <Grid2 size={4}>
                <Typography
                  fontSize={12}
                  fontWeight={500}
                  color="var(--neutral-500)"
                >
                  First Subscription Date
                </Typography>
                <Typography fontWeight={500} color="#4F4F4F">
                  {matchDate(
                    data?.data?.data?.subscriptionDetails
                      ?.firstSubscriptionDate,
                  )}
                </Typography>
              </Grid2>
              <Grid2 size={4}>
                <Typography
                  fontSize={12}
                  fontWeight={500}
                  color="var(--neutral-500)"
                >
                  Last Renewal Date
                </Typography>
                <Typography fontWeight={500} color="#4F4F4F">
                  {matchDate(
                    data?.data?.data?.subscriptionDetails?.lastRenewalDate,
                  )}
                </Typography>
              </Grid2>
              <Grid2 size={4}>
                <Typography
                  fontSize={12}
                  fontWeight={500}
                  color="var(--neutral-500)"
                >
                  Next Renewal Date
                </Typography>
                <Typography fontWeight={500} color="#4F4F4F">
                  {matchDate(
                    data?.data?.data?.subscriptionDetails?.nextRenewalDate,
                  )}
                </Typography>
              </Grid2>
            </Grid2>
          </Card>
        </Grid2>

        <Show.When
          isTrue={subscriptionList.length !== 0}
          showNodata
          textContent="Not data found!"
        >
          <Grid2
            container
            size={12}
            maxHeight={`calc(100vh - 395px)`}
            overflow={"auto"}
            spacing={2}
          >
            {subscriptionList?.map(
              (dataItem: ClientBasketSubscriptionDetails) => {
                const {
                  invoiceDate,
                  frequency,
                  endDate,
                  autoRenewal,
                  paymentMode,
                  invoicePdf,
                  withGstAmount,
                } = dataItem;
                return (
                  <Grid2
                    size={12}
                    border={"1px solid #D5DEE5"}
                    borderRadius={3}
                  >
                    <Grid2
                      container
                      spacing={1}
                      bgcolor={"#F7FBFD"}
                      borderRadius={3}
                      p={2}
                    >
                      <Grid2
                        size={12}
                        display={"flex"}
                        alignItems={"center"}
                        justifyContent={"space-between"}
                      >
                        <Typography fontSize={13} fontWeight={600}>
                          {matchDate(invoiceDate)}
                        </Typography>
                        <Typography fontSize={16} fontWeight={600}>
                          {numberToCurrency(withGstAmount)}
                        </Typography>
                      </Grid2>
                      <Grid2 size={12}>
                        <Typography
                          color="var(--neutral-500)"
                          fontSize={12}
                          fontWeight={500}
                          display={"flex"}
                          alignItems={"center"}
                          gap={0.5}
                        >
                          {frequency}:
                          <Typography
                            fontSize={12}
                            fontWeight={500}
                            color="#222"
                          >
                            {matchDate(invoiceDate)} - {matchDate(endDate)}
                          </Typography>
                        </Typography>
                      </Grid2>
                    </Grid2>
                    <Grid2 size={12}>
                      <Divider />
                    </Grid2>
                    <Grid2 container size={12} spacing={2} p={2}>
                      <Grid2
                        size={12}
                        display={"flex"}
                        alignItems={"center"}
                        gap={1}
                        mt={3}
                      >
                        <Typography
                          fontSize={12}
                          fontWeight={500}
                          color={"var(--neutral-500)"}
                          display={"flex"}
                          alignItems={"center"}
                          gap={0.5}
                        >
                          <ReloadIcon height={14} width={14} />
                          Auto Renewal:
                        </Typography>
                        <Typography fontSize={12} fontWeight={500} color="#333">
                          {autoRenewal ? "On" : "Off"}
                        </Typography>
                      </Grid2>
                      <Grid2
                        size={12}
                        display={"flex"}
                        alignItems={"center"}
                        gap={1}
                      >
                        <Typography
                          fontSize={12}
                          fontWeight={500}
                          color={"var(--neutral-500)"}
                          display={"flex"}
                          alignItems={"center"}
                          gap={0.5}
                        >
                          <RupeeIcon height={14} width={14} />
                          Mode of Payment:
                        </Typography>
                        <Typography fontSize={12} fontWeight={500} color="#333">
                          {paymentMode}
                        </Typography>
                      </Grid2>
                      <Grid2
                        size={12}
                        display={"flex"}
                        alignItems={"center"}
                        justifyContent={"flex-end"}
                        mt={1}
                      >
                        <GKButton
                          variant="contained"
                          startIcon={<DownloadIcon />}
                          onClick={() =>
                            handleDownloadFile({
                              link: invoicePdf,
                              name: `invoice_${dataItem.invoiceNumber}`,
                            })
                          }
                        >
                          Invoice
                        </GKButton>
                      </Grid2>
                    </Grid2>
                  </Grid2>
                );
              },
            )}
          </Grid2>
        </Show.When>
      </Grid2>
    </GKModal>
  );
};

export default SubscriptionDetailsModal;
